import { useMemo } from 'react';
import { useCentralizedProviderData } from '@crehana/auth';
import { usePathname } from "./use-pathname";

// Routes not allowed for Hana AI access
// Dynamic pattern for any organization
var disallowedRoutes = ['/org/[slug]/ai/admin/chat', '/org/[slug]/ai/user/chat', '/org/[slug]/panel/hana-ai'];

/**
 * Custom hook to check if the current route is disallowed for Hana AI access
 * @returns Object containing disallowed route information and utilities
 */
export var useDisallowedRoute = function useDisallowedRoute() {
  var _me$security, _me$security$learning;
  var pathname = usePathname();
  var _useCentralizedProvid = useCentralizedProviderData(),
    me = _useCentralizedProvid.me;
  var slug = me === null || me === void 0 ? void 0 : (_me$security = me.security) === null || _me$security === void 0 ? void 0 : (_me$security$learning = _me$security.learningSession) === null || _me$security$learning === void 0 ? void 0 : _me$security$learning.organizationSlug;

  /**
   * Check if the current route is in the disallowed routes list
   * @param currentPathname - The pathname to check (optional, defaults to current pathname)
   * @param currentSlug - The organization slug to check (optional, defaults to current slug)
   * @returns boolean indicating if the route is disallowed
   */
  var isRouteDisallowed = useMemo(function () {
    return disallowedRoutes.some(function (disallowedRoute) {
      // If the route contains [slug] and we have a slug, replace it
      if (disallowedRoute.includes('[slug]') && slug) {
        var routeWithSlug = disallowedRoute.replace('[slug]', slug);
        return pathname.includes(routeWithSlug);
      }
      // If it doesn't contain [slug], do a direct match
      return pathname.includes(disallowedRoute);
    });
  }, [pathname, slug]);

  /**
   * Check if a specific route is disallowed
   * @param routeToCheck - The route to check
   * @param slugToCheck - The slug to use for dynamic routes
   * @returns boolean indicating if the route is disallowed
   */
  var checkRouteDisallowed = function checkRouteDisallowed(routeToCheck, slugToCheck) {
    return disallowedRoutes.some(function (disallowedRoute) {
      // If the route contains [slug] and we have a slug, replace it
      if (disallowedRoute.includes('[slug]') && slugToCheck) {
        var routeWithSlug = disallowedRoute.replace('[slug]', slugToCheck);
        return routeToCheck.includes(routeWithSlug);
      }
      // If it doesn't contain [slug], do a direct match
      return routeToCheck.includes(disallowedRoute);
    });
  };
  return {
    isRouteDisallowed: isRouteDisallowed,
    checkRouteDisallowed: checkRouteDisallowed,
    currentPathname: pathname,
    currentSlug: slug,
    disallowedRoutes: disallowedRoutes
  };
};